import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { Alchemy, Network } from "alchemy-sdk";
import EXIF from "exif-js";
import "./MainNFTList.scss";
import ArchModal from "../ArchModal/ArchModal";
import PdfViewer from "../PdfViewer/PdfViewer";
import CollectionMetadataDisplay from "../CollectionMetadataDisplay/CollectionMetadataDisplay";
import { convertIpfsToUrl, fetchTokenMetadata } from "../../../utils";

const ALCHEMY_API_KEY = "cBHgm4PNLXlDi1jcTDugv";
const CONTRACT_ADDRESS = "0x0869A61f600138a27D649014750d322c6Ed203B3";

const MainNFTList = ({ aesKey }) => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contractMetadata, setContractMetadata] = useState(null);
  const [selectedNFT, setSelectedNFT] = useState(null);

  const openModal = (nft) => setSelectedNFT(nft);
  const closeModal = () => setSelectedNFT(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchContractData = async () => {
      setLoading(true);
      setNfts([]);
      setContractMetadata(null);

      try {
        const alchemy = new Alchemy({
          apiKey: ALCHEMY_API_KEY,
          network: Network.BASE_MAINNET,
        });

        const provider = new ethers.JsonRpcProvider(
          "https://base-mainnet.g.alchemy.com/v2/cBHgm4PNLXlDi1jcTDugv"
        );

        const response = await alchemy.nft.getNftsForContract(
          CONTRACT_ADDRESS,
          { pageSize: 100 }
        );
        if (isCancelled) return;

        const nftsWithMetadata = await Promise.all(
          response.nfts.map(async (nft) => {
            let metadata = nft.rawMetadata;

            const tokenUri =
              typeof nft.tokenUri === "string"
                ? nft.tokenUri
                : nft.tokenUri?.raw || nft.tokenUri?.gateway;

            if ((!metadata || !metadata.image) && tokenUri) {
              metadata = await fetchTokenMetadata(
                tokenUri,
                nft.tokenId,
                aesKey
              );
            }

            let imageUrl = null;
            let isPdf = false;
            let pdfUrl = null;

            if (metadata?.image) {
              const imageUri = metadata.image;
              imageUrl = convertIpfsToUrl(imageUri);

              isPdf =
                imageUri.toLowerCase().includes(".pdf") ||
                imageUrl.toLowerCase().includes(".pdf") ||
                metadata.mediaType === "pdf" ||
                (metadata.properties &&
                  metadata.properties.find(
                    (p) => p.trait_type === "Collection" && p.value
                  ));

              if (isPdf) pdfUrl = imageUrl;
            }

            return {
              ...nft,
              metadata,
              imageUrl,
              isPdf,
              pdfUrl,
              title: metadata?.name || `Token #${nft.tokenId}`,
              description: metadata?.description || "",
            };
          })
        );

        if (!isCancelled) setNfts(nftsWithMetadata.slice(0, 5));
      } catch (err) {
        console.error("[CONTRACT] Error:", err);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchContractData();
    return () => (isCancelled = true);
  }, [aesKey]);

  const handleImageLoad = (e) => {
    const img = e.target;
    EXIF.getData(img, function () {
      const orientation = EXIF.getTag(this, "Orientation");
      switch (orientation) {
        case 2:
          img.style.transform = "scaleX(-1)";
          break;
        case 3:
          img.style.transform = "rotate(180deg)";
          break;
        case 4:
          img.style.transform = "rotate(180deg) scaleX(-1)";
          break;
        case 5:
          img.style.transform = "rotate(90deg) scaleY(-1)";
          break;
        case 6:
          img.style.transform = "rotate(90deg)";
          break;
        case 7:
          img.style.transform = "rotate(-90deg) scaleY(-1)";
          break;
        case 8:
          img.style.transform = "rotate(-90deg)";
          break;
        default:
          img.style.transform = "none";
      }
    });
  };

  return (
    <div>
      {contractMetadata && (
        <div className="PreCard">
          <CollectionMetadataDisplay metadata={contractMetadata} />
        </div>
      )}

      {loading && (
        <div className="arch_d_loading">
          <div className="arch_d_loading__spinner"></div>
          <p className="arch_d_loading__text">Loading NFTs...</p>
        </div>
      )}

      {nfts.length > 0 && (
        <>
          <div className="arch-Category-content">
            {nfts.map((nft) => (
              <div
                key={`${CONTRACT_ADDRESS}-${nft.tokenId}`}
                className="arch_d_nft-card"
              >
                <div className="arch_d_nft-card__image-wrapper">
                  <div className="arch_d_nft-card__badge">
                    <div className="arch_d_nft-card__content">
                      <span className="arch_d_nft-card__year">
                        {nft.collection?.name?.match(/\d{4}/)?.[0] || ""}
                      </span>
                      <span className="arch_d_nft-card__label">Palestine</span>
                    </div>
                  </div>

                  {nft.isPdf && nft.pdfUrl ? (
                    <PdfViewer pdfUrl={nft.pdfUrl} height={'100%'} objectFit={'cover'}/>
                  ) : nft.imageUrl ? (
                    <img
                      src={nft.imageUrl}
                      alt={nft.title}
                      className="arch_d_nft-card__image"
                      draggable="false"
                      onLoad={handleImageLoad}
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  ) : (
                    <div className="arch_d_nft-card__placeholder">
                      <span>No media</span>
                    </div>
                  )}
                </div>

                <div className="arch_d_nft-card__header">
                  <div className="arch_d_nft-card__field">
                    <h4 className="arch_d_nft-card__title">{nft.title}</h4>
                    <span>
                      {" "}
                      {nft.collection?.name?.match(/\d{4}/)?.[0] || ""}
                    </span>
                  </div>
                  <button className="buy-btn" onClick={() => openModal(nft)}>
                    Read
                  </button>
                </div>
              </div>
            ))}
          </div>

          {selectedNFT && <ArchModal item={selectedNFT} onClose={closeModal} />}
        </>
      )}
    </div>
  );
};

export default MainNFTList;
